import {
    PhoebeAgentEventFeedChatSurface,
    usePhoebeAgentEventFeedChatViewState,
} from '@apps/web/components/phoebe_chat_view';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Spinner } from '@apps/web/components/ui/spinner';
import { usePhoebeAgentRunEventStream } from '@apps/web/hooks/use_phoebe_agent_run_event_stream';
import { runTitle } from '@apps/web/lib/training_run_utils';
import { type TrainingRunRead } from '@libraries/typescript/api_client';

// Read-only view of a past chat run, streamed from the org-scoped run
// events endpoint (same access clause as the training feed). Deliberately
// not the docked general chat: that surface is writable and persists its
// selection, so loading a review run there would leak it into the
// coordinator's active session.
export function ChatRunTranscriptDialog({
    run,
    organizationId,
    onClose,
}: {
    readonly run: TrainingRunRead;
    readonly organizationId: string;
    readonly onClose: () => void;
}) {
    const stream = usePhoebeAgentRunEventStream({
        organizationId,
        phoebeAgentRunId: run.id,
    });
    const chatState = usePhoebeAgentEventFeedChatViewState({
        events: stream.events,
        pendingEvents: stream.pendingEvents,
        fallbackRunStatus: stream.runState?.status ?? run.status,
        phoebeAgentRunId: run.id,
        liveEventIds: stream.liveEventIds,
    });
    const hasSnapshot =
        stream.runState !== null ||
        stream.events.length > 0 ||
        stream.pendingEvents.length > 0;

    return (
        <Dialog
            open={true}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
        >
            <DialogContent className="flex h-[80vh] max-w-3xl flex-col gap-3">
                <DialogHeader>
                    <DialogTitle className="truncate pr-6">
                        {runTitle(run)}
                    </DialogTitle>
                </DialogHeader>
                <div className="min-h-0 flex-1 overflow-hidden rounded-lg border">
                    {stream.isLoading && !hasSnapshot ? (
                        <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
                            <Spinner size="sm" />
                            Loading chat…
                        </div>
                    ) : stream.error && !hasSnapshot ? (
                        <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                            Couldn’t load this chat.
                        </div>
                    ) : (
                        <PhoebeAgentEventFeedChatSurface
                            state={chatState}
                            onSubmit={() => false}
                            readOnly={true}
                            organizationId={organizationId}
                            emptyState="No messages in this chat."
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
